import { useTableFilter, useWindowSize } from '@welbe/web-design-system';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getHraGroupsEmployees } from 'Views/HealthRiskAssessment/HealthRiskAssessment.services';
import { HealthRiskAssessmentContext } from '../../HealthRiskAssessment.context';
import { formatRows } from './Population.builder';
import { GROUPS, headers } from './Population.constants';
import {
	ParamsProps,
	PatientProps,
	PatientResponse,
	TableHeaderProps,
} from './Population.types';

export const usePopulation = () => {
	const { height } = useWindowSize();
	const [tableHeaders, setTableHeaders] = useState<TableHeaderProps[]>(headers);
	const { assignedGroups, selectedGroup, setSelectedGroup } = useContext(
		HealthRiskAssessmentContext,
	);
	const { companyId } = useParams() as ParamsProps;
	const [isTableLoading, setTableLoading] = useState<boolean>(false);
	const [isFilterOpen, setFilterOpen] = useState<boolean>(true);
	const [selectedEmployees, setSelectedEmployees] = useState<PatientResponse[]>(
		[],
	);
	const [isDrawerEmployeeVisible, setDrawerEmployeeVisible] =
		useState<boolean>(false);

	const onToggleFilter = () => setFilterOpen((prev) => !prev);

	const formatDataRows = (data: PatientResponse[]): PatientProps[] => {
		return data?.map(formatRows) || [];
	};

	const getTableHeaders = async (name: string) => {
		try {
			setTableLoading(true);
			const defaultGroupName = GROUPS[name as keyof typeof GROUPS];

			const updatedHeaders = headers.map((header) =>
				header?.id === 'reference'
					? { ...header, label: defaultGroupName }
					: header,
			);

			setTableHeaders(updatedHeaders);
			window.setTimeout(() => setTableLoading(false), 500);
		} catch (error) {
			console.error(error);
		}
	};

	const {
		data,
		loading,
		filters,
		currentPage,
		totalPages,
		handleSearch,
		handleFilters,
		handleChangePage,
	} = useTableFilter({
		service: getHraGroupsEmployees,
		preventFirstRender: true,
		instantSearch: ['riskLevel', 'hraGroupCompanyId'],
		tableFilters: {
			companyId,
			hraGroupCompanyId: selectedGroup?.hraGroupCompanyId || '',
		},
	});

	const handleCloseDrawerEmployees = (refetch: boolean) => {
		setDrawerEmployeeVisible((prev) => !prev);
		if (refetch) {
			handleSearch();
		}
	};

	useEffect(() => {
		const group = assignedGroups.find(
			({ hraGroupCompanyId }) =>
				hraGroupCompanyId === filters.hraGroupCompanyId,
		);
		const name = group?.name || 'reference';

		getTableHeaders(name);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters.hraGroupCompanyId]);

	useEffect(() => {
		if (!selectedGroup?.hraGroupCompanyId) {
			const [firstGroup] = assignedGroups;
			setSelectedGroup(firstGroup);
			handleFilters({
				hraGroupCompanyId: firstGroup?.hraGroupCompanyId || '',
			});
		} else {
			handleSearch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		data,
		height,
		filters,
		loading,
		totalPages,
		currentPage,
		tableHeaders,
		isFilterOpen,
		assignedGroups,
		isTableLoading,
		handleSearch,
		handleFilters,
		onToggleFilter,
		formatDataRows,
		handleChangePage,
		selectedGroup,
		selectedEmployees,
		setSelectedEmployees,
		isDrawerEmployeeVisible,
		handleCloseDrawerEmployees,
	};
};
